import {
  Button,
  Form,
  Modal,
  message,
  Menu,
  Dropdown,
  Typography,
  Tooltip,
} from "antd"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import apiClient from "../../../Utils/apiClient"
import "./GroupDevices.scss"
import "../../Operations/Operations.scss"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import HostnameLink from "../../../components/HostnameLink/HostnameLink"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import {
  ExclamationCircleOutlined,
  MoreOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import { AddCircle } from "iconsax-react"
import AutoCompleteBox from "../../../components/AutoCompleteBox/AutoCompleteBox"
const { Text } = Typography
const { confirm } = Modal

const GROUP_VIEWS = [
  { key: "all", label: "Groups", tone: "#2563eb", soft: "rgba(59, 130, 246, 0.14)" },
  { key: "online", label: "With online devices", tone: "#059669", soft: "rgba(34, 197, 94, 0.14)" },
  { key: "away", label: "With away devices", tone: "#d97706", soft: "rgba(245, 158, 11, 0.16)" },
  { key: "offline", label: "With offline devices", tone: "#dc2626", soft: "rgba(248, 113, 113, 0.14)" },
  { key: "empty", label: "Empty groups", tone: "#64748b", soft: "rgba(148, 163, 184, 0.12)" },
]

const SIZE_CHIPS = [
  { value: "", label: "All" },
  { value: "small", label: "1–10" },
  { value: "large", label: "10+" },
]

const statusCount = (group, status) =>
  (group?.devices || []).filter((d) => d.deviceStatus === status).length

const matchesGroupView = (group, key) => {
  if (key === "all") return true
  if (key === "empty") return !(group?.devices || []).length
  if (key === "online") return statusCount(group, "Online") > 0
  if (key === "away") return statusCount(group, "Away") > 0
  if (key === "offline") return statusCount(group, "Offline") > 0
  return true
}

const matchesSize = (group, size) => {
  const count = (group?.devices || []).length
  if (size === "small") return count >= 1 && count <= 10
  if (size === "large") return count > 10
  return true
}

const GroupDevices = () => {
  const page = `Groups and Devices`
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [render, setRender] = useState(false)
  const [view, setView] = useState("all")
  const [sizeFilter, setSizeFilter] = useState("")
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })

  const [filters, setFilters] = useState({
    hostname: "",
    name: "",
    deviceStatus: "",
  })
  const handleClearFilter = () => {
    setFilters({
      hostname: "",
      name: "",
      status: "",
    })
    getData(1, paginationData.pageSize)
    setRender((pre) => !pre)
  }
  const navigate = useNavigate()
  const [form] = Form.useForm()

  useEffect(() => {
    getData(1, paginationData.pageSize, filters)
  }, [render])

  const getData = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize
  ) => {
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        ...filters,
      }
      setLoading(true)
      const res = await apiClient("device-grouping/get", "POST", params)
      if (res) {
        setData(res?.result)
      }
      setLoading(false)
    } catch (err) {
      console.log("err =========>", err)
    }
  }

  const handleCloseModal = () => {
    form.resetFields()
  }

  const handleOk = async () => {
    await form
      .validateFields()
      .then(async (values) => {
        console.log(values)

        const res = await apiClient("device-grouping/add", "POST", {
          name: values.groupName,
          requiredApplicationsGroup: values.appGroup,
        })
        if (res.success) {
          message.success("Group added successfully")
          setRender(!render)
          handleCloseModal()
        }
      })
      .catch((info) => {
        console.log("Validation Failed:", info)
      })
  }

  const handleEdit = (record) => {
    // Prefill for edit mode (if needed in future)
    console.log("record ==>", record)

    form.setFieldsValue({
      groupName: record.name,
    })
    navigate(`/edit/group-devices/${record?._id}`)
  }

  const handleDelete = (record) => {
    confirm({
      title: "Are you sure you want to delete this group?",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            Group Name: <strong>{record.name}</strong>
          </p>
          {record.devicesCount > 0 && (
            <p style={{ color: "#ff4d4f" }}>
              This group contains {record.devicesCount} device(s).
            </p>
          )}
        </div>
      ),
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setLoading(true)
          const res = await apiClient(
            `device-grouping/delete/${record._id || record?.id}`,
            "DELETE"
          )
          if (res.success) {
            message.success(res.message)
            getData(1, paginationData.pageSize, filters)
          }
        } catch (err) {
          message.warning(err.message)
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const groupSummary = useMemo(() => {
    const groups = data || []
    const summary = {}
    GROUP_VIEWS.forEach((card) => {
      summary[card.key] = groups.filter((g) => matchesGroupView(g, card.key)).length
    })
    summary.devices = groups.reduce((sum, g) => sum + (g.devices || []).length, 0)
    return summary
  }, [data])

  const visibleGroups = useMemo(
    () =>
      (data || []).filter(
        (group) => matchesGroupView(group, view) && matchesSize(group, sizeFilter)
      ),
    [data, view, sizeFilter]
  )

  const columns = [
    {
      title: "Group Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <button
          type="button"
          className="ops-title"
          onClick={() => navigate(`/group-devices-lists/${record._id}`)}
        >
          <span className="ops-title__name ops-name">{text}</span>
          <span className="ops-title__kb">
            {(record.devices || []).length} device
            {(record.devices || []).length === 1 ? "" : "s"}
          </span>
        </button>
      ),
    },
    {
      title: "Devices",
      dataIndex: "devices",
      key: "devices",
      render: (devices) => {
        if (!devices || devices.length === 0) {
          return <span className="ops-muted">No devices</span>
        }
        const preview = devices.slice(0, 2)
        const extra = devices.slice(2)
        return (
          <div className="inv-device__copy">
            {preview.map((device) => (
              <div className="inv-device__title" key={device._id || device.hostname}>
                <span
                  className={`inv-device__pulse inv-device__pulse--${String(
                    device.deviceStatus || "offline"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  aria-hidden
                />
                <HostnameLink record={device}>{device.hostname}</HostnameLink>
              </div>
            ))}
            {extra.length ? (
              <Tooltip
                title={
                  <pre style={{ margin: 0 }}>
                    {extra.map((d) => d.hostname).join("\n")}
                  </pre>
                }
              >
                <span className="inv-device__meta">+{extra.length} more</span>
              </Tooltip>
            ) : null}
          </div>
        )
      },
    },
    {
      title: "Online",
      dataIndex: "devices",
      key: "online",
      render: (devices) => {
        if (!devices || devices.length === 0) return "0"

        const onlineDevices = devices.filter((d) => d.deviceStatus === "Online")
        if (onlineDevices.length === 0) return "0"

        const hostnames = onlineDevices.map((d) => d.hostname).join("\n")
        return (
          <Tooltip title={<pre style={{ margin: 0 }}>{hostnames}</pre>}>
            <span style={{ color: "green", cursor: "pointer" }}>
              {onlineDevices.length}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: "Offline",
      dataIndex: "devices",
      key: "offline",
      render: (devices) => {
        if (!devices || devices.length === 0) return "0"

        const offlineDevices = devices.filter(
          (d) => d.deviceStatus === "Offline"
        )
        if (offlineDevices.length === 0) return "0"

        const hostnames = offlineDevices.map((d) => d.hostname).join("\n")
        return (
          <Tooltip title={<pre style={{ margin: 0 }}>{hostnames}</pre>}>
            <span style={{ color: "red", cursor: "pointer" }}>
              {offlineDevices.length}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: "Away",
      dataIndex: "devices",
      key: "away",
      render: (devices) => {
        if (!devices || devices.length === 0) return "0"

        const awayDevices = devices.filter((d) => d.deviceStatus === "Away")
        if (awayDevices.length === 0) return "0"

        const hostnames = awayDevices.map((d) => d.hostname).join("\n")
        return (
          <Tooltip title={<pre style={{ margin: 0 }}>{hostnames}</pre>}>
            <span style={{ color: "orange", cursor: "pointer" }}>
              {awayDevices.length}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="edit" onClick={() => handleEdit(record)}>
              Edit
            </Menu.Item>
            <Menu.Item key="delete" onClick={() => handleDelete(record)}>
              Delete
            </Menu.Item>
          </Menu>
        )
        return (
          <Dropdown overlay={menu} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  return (
    <PanelLayout
      title="Groups and Devices"
      subtitle="Organize endpoints into groups for targeted updates and policy."
    >
      <div className="groups">
        <div className="ops-kpis">
          <div
            className="ops-kpi ops-kpi--static"
            style={{ "--act-tone": "#7c3aed", "--act-soft": "rgba(167, 139, 250, 0.14)" }}
          >
            <div>
              <span className="ops-kpi-label">Devices in groups</span>
              <strong>{groupSummary.devices}</strong>
            </div>
          </div>
          {GROUP_VIEWS.map((card) => (
            <button
              type="button"
              key={card.key}
              className={`ops-kpi${view === card.key ? " is-active" : ""}`}
              style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
              onClick={() => setView(card.key)}
            >
              <div>
                <span className="ops-kpi-label">{card.label}</span>
                <strong>{groupSummary[card.key] ?? 0}</strong>
              </div>
            </button>
          ))}
        </div>

        <div className="ops-toolbar">
          <span className="ops-toolbar__count">
            {visibleGroups.length} of {data?.length || 0} group
            {(data?.length || 0) === 1 ? "" : "s"}
            {view !== "all"
              ? ` · ${GROUP_VIEWS.find((c) => c.key === view)?.label}`
              : ""}
          </span>
          <div className="dashboard-filter-chips">
            <span>Size</span>
            {SIZE_CHIPS.map((chip) => (
              <Button
                key={chip.value || "all-size"}
                size="small"
                type={sizeFilter === chip.value ? "primary" : "default"}
                onClick={() => setSizeFilter(chip.value)}
              >
                {chip.label}
              </Button>
            ))}
          </div>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => getData(paginationData.page, paginationData.pageSize)}
          >
            Refresh
          </Button>
        </div>

        <TableBox
          title="Group devices"
          className="dashboard-inventory-table"
          extra={
            <Button
              icon={<AddCircle size={15} />}
              type="primary"
              onClick={() => navigate("/add/group-devices")}
            >
              Add Group
            </Button>
          }
          page={page}
          columns={columns}
          loading={loading}
          data={visibleGroups}
          pagination={{
            total: paginationData.total,
            current: paginationData.page,
            pageSize: paginationData.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            onChange: (page, pageSize) => {
              setPaginationData((prev) => ({ ...prev, page, pageSize }))
            },
          }}
          enableFilter
          filtercontent={
            <div className="p-3">
              <AutoCompleteBox
                label="Hostname"
                name="hostname"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search Hostname"
                value={filters.hostname}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, hostname: value }))
                }
              />
              <AutoCompleteBox
                label="Name"
                name="name"
                className="mb-3 w-100"
                endPoint="group-devices"
                placeholder="Search Title"
                value={filters.name}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, name: value }))
                }
              />

              <AutoCompleteBox
                label="Status"
                name="deviceStatus"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search Status"
                value={filters.deviceStatus}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, deviceStatus: value }))
                }
              />
            </div>
          }
          onFilter={() => {
            setPaginationData((prev) => ({ ...prev, page: 1 }))
            getData(1, paginationData.pageSize, filters)
          }}
          onClear={handleClearFilter}
          filters={filters}
        />
      </div>
    </PanelLayout>
  )
}

export default GroupDevices
